import React from 'react';
import { Camera, Check, ChefHat, ShoppingCart } from 'lucide-react';

const HowItWorks = ({ language }) => {
  const texts = {
    en: {
      title: 'How Does Souschaif Work?',
      subtitle: 'Four simple steps to cook smarter with what you already have',
      steps: [
        {
          number: '01',
          icon: Camera,
          title: 'Snap a Photo',
          description: 'Take a quick picture of your fridge or pantry. Souschaif recognizes your ingredients automatically.'
        },
        {
          number: '02',
          icon: Check,
          title: 'Receive Personalized Suggestions',
          description: 'Get recipes tailored to your ingredients, allergies, preferences, and nutritional needs.'
        },
        {
          number: '03',
          icon: ChefHat,
          title: 'Cook with Confidence',
          description: 'Follow clear instructions and make the most of every ingredient you already own.'
        },
        {
          number: '04',
          icon: ShoppingCart,
          title: 'Shop Smarter',
          description: 'If something is missing, Souschaif suggests alternatives or builds a focused shopping list.'
        }
      ]
    },
    sl: {
      title: 'Kako deluje Souschaif?',
      subtitle: 'Štirje preprosti koraki za pametnejše kuhanje iz sestavin, ki jih že imate',
      steps: [
        {
          number: '01',
          icon: Camera,
          title: 'Fotografirajte',
          description: 'Hitro fotografirajte hladilnik ali shrambo. Souschaif samodejno prepozna sestavine.'
        },
        {
          number: '02',
          icon: Check,
          title: 'Prejmite osebne predloge',
          description: 'Dobite recepte glede na sestavine, alergije, želje in prehranske potrebe.'
        },
        {
          number: '03',
          icon: ChefHat,
          title: 'Kuhajte samozavestno',
          description: 'Sledite jasnim navodilom in čim bolje uporabite vsako sestavino, ki jo že imate.'
        },
        {
          number: '04',
          icon: ShoppingCart,
          title: 'Nakupujte pametneje',
          description: 'Če kaj manjka, Souschaif predlaga alternative ali pripravi osredotočen nakupovalni seznam.'
        }
      ]
    }
  };

  const t = texts[language];

  return (
    <section id="section-3" className="py-20 bg-gradient-to-br from-emerald-50 via-lime-50 to-green-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.steps.map((step, index) => (
            <div
              key={index}
              className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-emerald-600 text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-lg">
                {step.number}
              </div>
              
              <div className="w-20 h-20 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                <step.icon className="w-10 h-10 text-emerald-600" />
              </div>
              
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                {step.title}
              </h3>
              
              <p className="text-gray-600 leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* Process Image */}
        <div className="mt-20 relative">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-4 bg-gradient-to-r from-emerald-400 to-lime-400 rounded-3xl opacity-20 blur-xl"></div>
              <div className="relative bg-white rounded-2xl p-6 shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1200&q=80"
                  alt={language === 'en' ? 'Cooking with fresh ingredients in a bright kitchen' : 'Kuhanje s svežimi sestavinami v svetli kuhinji'}
                  className="w-full h-[380px] object-cover rounded-xl"
                />
                
                {/* Animated progress indicator */}
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-lg px-4 py-3 shadow-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-600">
                      {language === 'en' ? 'Food waste saved' : 'Prihranjena odpadna hrana'}
                    </span>
                    <span className="text-xs font-bold text-emerald-600">85%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-gradient-to-r from-emerald-600 to-lime-500 h-2 rounded-full animate-progress" style={{width: '85%'}}></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2 space-y-4">
              <div className="inline-flex items-center space-x-2 bg-emerald-100 rounded-full px-4 py-2">
                <Check className="w-5 h-5 text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-600">
                  {language === 'en' ? 'Smart & Sustainable' : 'Pametno in trajnostno'}
                </span>
              </div>
              <h3 className="text-3xl font-bold text-gray-900">
                {language === 'en' ? 'Meal Ideas in Seconds' : 'Ideje za obroke v nekaj sekundah'}
              </h3>
              <p className="text-lg text-gray-600">
                {language === 'en' 
                  ? 'Souschaif takes the guesswork out of meal prep, helping you enjoy more and waste less.'
                  : 'Souschaif odpravi ugibanje pri pripravi obrokov, da lahko uživate več in zavržete manj.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
